import React from 'react'
import LinkIcon from '@material-ui/icons/Link'
import { toast } from 'react-toastify'
import IconButton from '@material-ui/core/IconButton'
import { merge, of, NEVER, BehaviorSubject } from 'rxjs'
import { map } from 'rxjs/operators'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { first, includes, last, map as _map, mapValues, pick, reduce, result } from 'libraries/lodash'
import {
  CURRENT_AWARENESS as CA,
  CURRENT_AWARENESS_PREPAY,
  CURRENT_AWARENESS_SEARCH,
  CURRENT_AWARENESS_SEARCH_PREPAY,
  CURRENT_AWARENESS_HOMEPAGE,
  CURRENT_AWARENESS_HOMEPAGE_ARTICLE,
  CURRENT_AWARENESS_HOMEPAGE_ARTICLE_PREPAY,
  CURRENT_AWARENESS_ARTICLE
} from 'constants/requests'
import url from 'front-end-core/core/url'
import { observePageState, updatePageState } from 'front-end-core/services/PageStateService'
import { observeSelected, toggleContent } from 'front-end-core/services/StateService'
import { mapResponse } from 'front-end-core/helpers/dataflow'
import { CURRENT_AWARENESS } from 'constants/controllers'
import { CA_SELECTION } from 'constants/feature-flags'
import { DATE_FORMAT, INPUT_DATE_FORMAT, LONG_DATE_FORMAT } from 'constants/locale'
import { observe, fetch } from 'services/ApiService'
import { accordionContent, accordionGroup } from 'helpers/accordion'
import notificationToast from 'helpers/notificationToast'
import loggedRequestSwitcher from 'helpers/loggedRequestSwitcher'
import selectAolName from 'redux/selectors/selectAolName'
import TealToolTip from 'components/TealToolTip'
import { dismissPopover } from './OverlayService'

const CA_STATE_KEY = 'currentawareness'

export const mapDate = function (date) {
  const today = dayjs().format(DATE_FORMAT)
  const yesterday = dayjs().subtract(1, 'day').format(DATE_FORMAT)
  const tomorrow = dayjs().add(1, 'day').format(DATE_FORMAT)

  if (today === date) {
    return 'Today'
  }
  if (yesterday === date) {
    return 'Yesterday'
  }
  if (tomorrow === date) {
    return 'Tomorrow'
  }

  return dayjs(date).format(LONG_DATE_FORMAT)
}

const toNewsItem = function ({ path, expanded, searchPhrase, reviewMode }, item, index) {
  const itemPath = [...path, item.id]
  const itemPathStr = itemPath.join('/')

  return accordionContent({
    content: item.content,
    id: itemPathStr,
    path: itemPath,
    imageUrl: item.imageUrl,
    prefix: (!reviewMode && expanded && index + 1) || '',
    stateSource: merge(
      expanded
        ? of({ expanded })
        : observePageState(itemPathStr).pipe(map(expanded => ({ expanded }))),
      !CA_SELECTION || reviewMode
        ? NEVER
        : observeSelected(CA_STATE_KEY, [itemPathStr]).pipe(map(r => ({ selected: result(r, [0, 'selected'], 0) })))
    ),
    suffix: reviewMode ? null : (<CaReviewTealToolTip {...{ searchPhrase, item }} />),
    title: item.title,
    position: item.position,
    trimmed: item.trimmed === 'true'
  })
}

const CaReviewTealToolTip = ({ searchPhrase, item }) => {
  const aol = useSelector(selectAolName)

  const urlText = url(CURRENT_AWARENESS, { aol, searchPhrase, id: item.id }, {}, true)

  return (
    <TealToolTip
      interactive
      arrow
      title='Copy unique article link to Clipboard'
    >
      <div className='flex items-center'>
        <a href={url(CURRENT_AWARENESS, { aol, searchPhrase, id: item.id })}>
          <IconButton
            size='small'
            data-clipboard-text={urlText}
            onClick={e => {
              dismissPopover(e.target)
              e.stopPropagation()
              e.preventDefault()
              // override clipboard js functionality
              navigator.clipboard.writeText(urlText)
              notificationToast(<LinkIcon />, toast.TYPE.INFO, 'Link copied to clipboard')
            }}
            data-event-category='Share'
            data-event-action='Link'
            data-event-label={urlText}
          >
            <LinkIcon className='black' />
          </IconButton>
        </a>
      </div>
    </TealToolTip>
  )
}

const toDays = function ({ fromDate, toDate, searchPhrase, flavour = 'full', id, reviewMode }, resp) {
  const _toDate = dayjs(toDate).subtract(1, 'day').format(INPUT_DATE_FORMAT)
  const _fromDate = dayjs(fromDate).format(INPUT_DATE_FORMAT)
  const path = [fromDate, toDate]
  if (Object.keys(resp).length === 0) {
    if (searchPhrase) {
      return [accordionContent({ content: '<i>Search returned no results.</i>', path, id: path.join('/') })]
    } else if (fromDate === _toDate && dayjs().format(DATE_FORMAT) === _toDate) {
      return [accordionContent({ content: '<i>FC current awareness features are uploaded real-time throughout the day.</i>', path, id: path.join('/') })]
    } else if (id) {
      return []
    } else if (fromDate === _toDate) {
      return [accordionContent({ content: `<i>There is no News on ${_toDate}.</i>`, path, id: path.join('/') })]
    }

    return [accordionContent({ content: `<i>There is no News between ${_fromDate} and ${_toDate}.</i>`, path, id: path.join('/') })]
  }

  const today = dayjs().format(DATE_FORMAT)
  const isArchive = fromDate !== today || toDate !== today
  const isSearch = !!searchPhrase

  if (id) {
    resp = mapValues(resp, (vs) => vs.filter(v => v.id === id))
    if (reduce(resp, (a, e) => a + e.length, 0) === 0) {
      throw new Error('No data found')
    }
  }

  const output = _map(resp,
    (items, date) => {
      const behaviorSubject = new BehaviorSubject(null)
      const mappedItems = items.map(
        toNewsItem.bind(
          null,
          {
            path: [date],
            expanded: !!reviewMode || !!id || (!(isArchive || searchPhrase) && 1) || 0,
            toDate,
            fromDate,
            searchPhrase,
            flavour,
            reviewMode
          }
        )
      )
      behaviorSubject.next(mappedItems)
      return items.length &&
      accordionGroup({
        id: `${date}/`,
        title: mapDate(date),
        path: [date],
        stateSource:
          !reviewMode && !id && (isSearch || isArchive)
            ? observePageState(date).pipe(map(expanded => ({ expanded: +!expanded })))
            : of({ expanded: 1 }),
        groupSource: behaviorSubject
      })
    }).filter(a => !!a)

  if (!isArchive) {
    output.unshift(
      accordionContent({ content: '<i>FC current awareness features are uploaded real-time throughout the day.</i>', path, id: path.join('/') })
    )
  }

  return output
}

export const requestGroup = function ({ fromDate, toDate, searchPhrase, flavour, id, aol }) {
  if (searchPhrase) {
    return requestSearch({ flavour, searchPhrase, aol })
  }
  if (id) {
    return requestArticle({ flavour, id, aol })
  }
  return requestAll({ fromDate, toDate, flavour, id, aol })
}

const requestAll = function ({ fromDate, toDate, flavour, id, aol }) {
  const loggedRequest = () => observe(CA, { fromDate, toDate, aol })
    .pipe(map(mapResponse(toDays.bind(null, { fromDate, toDate, flavour, id }))))
  const prepayRequest = () => observe(CURRENT_AWARENESS_PREPAY, { fromDate, toDate, aol })
    .pipe(map(mapResponse(toDays.bind(null, { fromDate, toDate, flavour, id }))))
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

const requestSearch = function ({ flavour, searchPhrase, aol }) {
  const loggedRequest = () => observe(CURRENT_AWARENESS_SEARCH, { searchPhrase, aol })
    .pipe(map(mapResponse(toDays.bind(null, { flavour, searchPhrase }))))
  const prepayRequest = () => observe(CURRENT_AWARENESS_SEARCH_PREPAY, { searchPhrase, aol })
    .pipe(map(mapResponse(toDays.bind(null, { flavour, searchPhrase }))))
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

const requestArticle = ({ flavour = 'full', id, aol }) =>
  observe(CURRENT_AWARENESS_ARTICLE, { flavour, id, aol })
    .pipe(map(mapResponse(toDays.bind(null, { flavour, id }))))

export const searchResultExpandCallback = function (pathStr, type, flavour, value) {
  switch (type) {
    case 'content':
      break
    case 'group':
      value = +!value
      break
  }
  updatePageState(state => Object.assign({}, state, { [pathStr]: value }))

  return false
}

export const selectCallback = function (path, type, flavour, value) {
  switch (type) {
    case 'content':
      toggleContent(value, CA_STATE_KEY, path)
      return true
  }
}

export const requestSelection = function (flavour, selectedIds, aol) {
  const dates = []
  const ids = []
  selectedIds.forEach(path => {
    const [date, id] = path.split(':')
    dates.push(date)
    ids.push(id)
  })
  dates.sort()
  const fromDate = first(dates)
  const toDate = dayjs(last(dates)).add(1, 'day').format(DATE_FORMAT)

  return observe(CURRENT_AWARENESS, { fromDate, toDate, aol }).pipe(
    map(mapResponse(
      res => mapValues(
        pick(res, dates),
        vs => vs.filter(v => includes(ids, v.id))
      )
    )),
    map(mapResponse(toDays.bind(null, { fromDate, toDate, flavour, reviewMode: true })))
  )
}

// TODO: nicer solution to stop the api getting spammed
export const requestHomePageArticles = (aol) => fetch(CURRENT_AWARENESS_HOMEPAGE, { aol })
  .pipe(
    map(mapResponse(toDays.bind(null, {})))
  )

export const requestHomePageArticleSingle = ({ id, aol }) => {
  const loggedRequest = () => observe(CURRENT_AWARENESS_HOMEPAGE_ARTICLE, { id, aol }).pipe(map(mapResponse(toDays.bind(null, {}))))
  const prepayRequest = () => observe(CURRENT_AWARENESS_HOMEPAGE_ARTICLE_PREPAY, { id, aol }).pipe(map(mapResponse(toDays.bind(null, {}))))
  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}
